import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle responses
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Generic HTTP methods
  get: (url) => apiClient.get(url),
  post: (url, data) => apiClient.post(url, data),
  put: (url, data) => apiClient.put(url, data),
  delete: (url) => apiClient.delete(url),

  // Auth
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  refreshToken: (token) => apiClient.post('/auth/refresh-token', { token }),

  // Products (public)
  getProducts: () => apiClient.get('/products'),
  getProduct: (id) => apiClient.get(`/products/${id}`),
  searchDomain: (domain) => apiClient.get(`/products/search/domain?domain=${domain}`),
  
  // HostShop Integration
  getHostshopURL: (productId) => {
    // Redirect URL to account.maestrohosts.com hostshop
    const baseURL = 'https://account.maestrohosts.com/hostshop';
    return productId ? `${baseURL}?product=${productId}` : baseURL;
  },

  // Domains (Porkbun - Direct registration in your account)
  searchDomains: (searchTerm) => apiClient.post('/domains/search', { searchTerm }),
  getDomainPricing: () => apiClient.get('/domains/pricing'),
  validateDomain: (domainName) => apiClient.post('/domains/validate', { domainName }),
  registerDomain: (data) => apiClient.post('/domains/register', data),
  renewDomain: (domainName, years) => apiClient.post(`/domains/${domainName}/renew`, { years }),
  transferDomain: (domainName, authCode) => apiClient.post(`/domains/${domainName}/transfer`, { authCode }),
  updateNameservers: (domainName, nameservers) => apiClient.put(`/domains/${domainName}/nameservers`, { nameservers }),
  lockDomain: (domainName) => apiClient.post(`/domains/${domainName}/lock`),
  unlockDomain: (domainName) => apiClient.post(`/domains/${domainName}/unlock`),

  // Customer Domains
  getCustomerDomains: () => apiClient.get('/customer-domains'),
  getCustomerDomain: (domain) => apiClient.get(`/customer-domains/${domain}`),
  toggleDomainLock: (domain, locked) => apiClient.put(`/customer-domains/${domain}/lock`, { locked }),
  toggleAutoRenew: (domain, enabled) => apiClient.put(`/customer-domains/${domain}/auto-renew`, { enabled }),
  getDomainEPPCode: (domain) => apiClient.get(`/customer-domains/${domain}/epp-code`),
  
  // DNS Management (PowerDNS)
  getDNSRecords: (domain) => apiClient.get(`/customer-domains/${domain}/dns`),
  addDNSRecord: (domain, record) => apiClient.post(`/customer-domains/${domain}/dns`, record),
  updateDNSRecord: (domain, recordId, updates) => apiClient.put(`/customer-domains/${domain}/dns/${recordId}`, updates),
  deleteDNSRecord: (domain, recordId) => apiClient.delete(`/customer-domains/${domain}/dns/${recordId}`),
  bulkImportDNS: (domain, format, content, replaceExisting) => 
    apiClient.post(`/customer-domains/${domain}/dns/bulk-import`, { format, content, replaceExisting }),
  getSampleCSV: () => apiClient.get('/customer-domains/bulk-import/sample-csv'),
  getSampleBIND: () => apiClient.get('/customer-domains/bulk-import/sample-bind'),

  // Profile
  getProfile: () => apiClient.get('/customers/profile'),
  updateProfile: (data) => apiClient.put('/customers/profile', data),
  getBalance: () => apiClient.get('/customers/balance'),

  // Hosting Accounts
  getHostingAccounts: () => apiClient.get('/hosting/accounts'),
  getHostingAccount: (id) => apiClient.get(`/hosting/accounts/${id}`),
  createHostingAccount: (data) => apiClient.post('/hosting/accounts', data),
  updateHostingAccount: (id, data) => apiClient.put(`/hosting/accounts/${id}`, data),
  deleteHostingAccount: (id) => apiClient.delete(`/hosting/accounts/${id}`),
  getHostingPlans: () => apiClient.get('/hosting/plans'),

  // Orders
  getOrders: () => apiClient.get('/orders'),
  getOrder: (id) => apiClient.get(`/orders/${id}`),
  createOrder: (data) => apiClient.post('/orders', data),
  processPayment: (orderId, data) => apiClient.post(`/orders/${orderId}/payment`, data),

  // Admin
  getCustomers: () => apiClient.get('/admin/customers'),
  getStatistics: () => apiClient.get('/admin/statistics'),
};

export default api;
